import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  Loader2,
  AlertCircle,
  Mail,
  CalendarClock,
  TrendingUp,
  Clock,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { ModalShell } from '@/components/ModalShell';
import { uploaderApi, type UploaderDetails } from '@/features/uploader/uploader.api';
import { useT, useLang, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';
import { formatRelative } from '@/lib/format';
import { UploaderScreeningDetailModal } from '@/pages/uploader/components/UploaderScreeningDetailModal';
import { actionCategory, categoryTone } from '@/features/audit/audit-actions';
import { humanizeAction, humanizeEntity } from '@/features/audit/humanize';

interface UploaderDetailModalProps {
  uploaderId: string | null;
  onClose: () => void;
}

type Tab = 'stats' | 'recentWork' | 'audit';

export function UploaderDetailModal({ uploaderId, onClose }: UploaderDetailModalProps): JSX.Element | null {
  const t = useT();
  const lang = useLang();
  const [tab, setTab] = useState<Tab>('stats');
  const [selectedScreening, setSelectedScreening] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['admin', 'uploader-details', uploaderId],
    queryFn: () => uploaderApi.getAdminUploaderDetails(uploaderId!),
    enabled: !!uploaderId,
    staleTime: 30_000,
  });

  const d = q.data;
  // HOTFIX #310 (crash en prod 2026-08-29): el early-return vivia ANTES de
  // este useMemo — al drillear, el conteo de hooks cambiaba entre renders
  // (React #310, pantalla rota). TODOS los hooks van antes de cualquier
  // return condicional. Regla de hooks, sin excepciones.
  const initials = useMemo(() => {
    const name = d?.user.fullName ?? '?';
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase())
      .join('') || '?';
  }, [d?.user.fullName]);

  if (!uploaderId) return null;

  const roleKey = ('admin.uploaderTeam.detail.role.' + (d?.user.role ?? 'uploader')) as StringKey;
  const roleLabel = d ? t(roleKey) : '';

  return (
    <ModalShell open={!!uploaderId} onClose={onClose} ariaLabelledBy="uploader-detail-title">
      <header className="sticky top-0 bg-white border-b border-nav-card px-6 py-4 flex items-start justify-between rounded-t-lg z-10 gap-3">
        <div className="min-w-0 flex-1 flex items-start gap-3">
          {/* Avatar */}
          <div
            className="h-12 w-12 rounded-full bg-nav-tealTint text-nav-tealDark flex items-center justify-center font-semibold text-base shrink-0"
            aria-label={t('admin.uploaderTeam.detail.avatarLabel')}
          >
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 id="uploader-detail-title" className="font-head text-[17px] font-bold text-nav-navy truncate">
                {d?.user.fullName ?? 'Loading…'}
              </h2>
              {d && (
                <>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]">
                    {roleLabel}
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border',
                      d.user.active
                        ? 'bg-nav-sageTint text-nav-sageText border-nav-sage'
                        : 'bg-nav-redTint text-nav-red border-[#e3b5ae]'
                    )}
                  >
                    {d.user.active ? t('admin.uploaderTeam.detail.activePill') : t('admin.uploaderTeam.detail.inactivePill')}
                  </span>
                </>
              )}
            </div>
            {d && (
              <p className="text-xs text-nav-slate truncate mt-1 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <Mail className="h-3 w-3" aria-hidden /> {d.user.email}
                </span>
                <span className="text-nav-faint">·</span>
                <span className="inline-flex items-center gap-1 text-nav-slate">
                  <CalendarClock className="h-3 w-3" aria-hidden />
                  {d.user.lastLoginAt
                    ? t('admin.uploaderTeam.detail.lastLogin', { when: formatRelative(d.user.lastLoginAt) })
                    : t('admin.uploaderTeam.detail.lastLoginNever')}
                </span>
              </p>
            )}
            {!d && <p className="text-xs text-nav-slate mt-0.5">{t('admin.uploaderTeam.detail.subtitle')}</p>}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="modal-scroll flex-1 overflow-y-auto font-body text-nav-navy">
        <div className="px-6 py-5 space-y-4">
          {/* Tab strip */}
          <div role="tablist" className="flex gap-1 border-b border-nav-card">
            <TabButton active={tab === 'stats'} onClick={() => setTab('stats')}>{t('admin.uploaderTeam.tab.stats')}</TabButton>
            <TabButton active={tab === 'recentWork'} onClick={() => setTab('recentWork')}>{t('admin.uploaderTeam.tab.recentWork')}</TabButton>
            <TabButton active={tab === 'audit'} onClick={() => setTab('audit')}>{t('admin.uploaderTeam.tab.audit')}</TabButton>
          </div>

          {q.isLoading && (
            <div className="py-10 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-nav-teal mx-auto" />
            </div>
          )}
          {q.error && (
            <div className="rounded-xl border border-[#e3b5ae] bg-nav-redTint px-4 py-3 text-sm text-nav-red flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">{t('admin.uploaderTeam.detail.couldNotLoad')}</p>
                <p className="text-xs">{(q.error as Error).message}</p>
              </div>
            </div>
          )}

          {d && tab === 'stats' && <StatsPanel d={d} t={t} />}
          {d && tab === 'recentWork' && <RecentWorkPanel d={d} t={t} onOpen={setSelectedScreening} />}
          {d && tab === 'audit' && <AuditPanel d={d} t={t} lang={lang} />}
        </div>
      </div>

      <footer className="sticky bottom-0 bg-white border-t border-nav-card px-6 py-4 flex justify-end gap-2 rounded-b-lg">
        <Button variant="ghost" onClick={onClose}>{t('admin.uploaderTeam.detail.close')}</Button>
      </footer>

      {selectedScreening && (
        <UploaderScreeningDetailModal
          screeningId={selectedScreening}
          onClose={() => setSelectedScreening(null)}
          readOnly
        />
      )}
    </ModalShell>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'text-nav-tealDark border-nav-teal'
          : 'text-nav-slate border-transparent hover:text-nav-navy'
      )}
    >
      {children}
    </button>
  );
}

// =====================================================================
// Stats panel
// =====================================================================

function StatsPanel({ d, t }: { d: UploaderDetails; t: ReturnType<typeof useT> }): JSX.Element {
  const windows = [
    { tk: 'admin.uploaderTeam.stats.window.today' as const, value: d.stats.today.uploaded },
    { tk: 'admin.uploaderTeam.stats.window.week' as const, value: d.stats.week.uploaded },
    { tk: 'admin.uploaderTeam.stats.window.month' as const, value: d.stats.month.uploaded },
    { tk: 'admin.uploaderTeam.stats.window.total' as const, value: d.stats.total.uploaded },
  ];

  const hourly = d.hourlyUploadsToday ?? [];
  const hourlyTotal = hourly.reduce((acc, h) => acc + h.count, 0);

  const myAvg = d.stats.today.avgTimeToUploadHours;
  const teamAvg = d.teamAvgTimeToUploadHours ?? 0;
  const delta = Math.round((teamAvg - myAvg) * 10) / 10;
  let vsTeam: string;
  if (myAvg === 0 && teamAvg === 0) {
    vsTeam = t('admin.uploaderTeam.stats.vsTeam.equal');
  } else if (delta > 0.1) {
    vsTeam = t('admin.uploaderTeam.stats.vsTeam.faster', { n: delta });
  } else if (delta < -0.1) {
    vsTeam = t('admin.uploaderTeam.stats.vsTeam.slower', { n: Math.abs(delta) });
  } else {
    vsTeam = t('admin.uploaderTeam.stats.vsTeam.equal');
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {windows.map((w) => (
          <div key={w.tk} className="rounded-xl bg-white border border-nav-card shadow-sm p-3">
            <p className="text-[11px] uppercase tracking-wide text-nav-slate">{t(w.tk)}</p>
            <p className="text-2xl font-semibold text-nav-navy tabular-nums mt-1">{w.value}</p>
            <p className="text-[11px] text-nav-slate">{t('admin.uploaderTeam.stats.uploadedToday').toLowerCase()}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat label={t('admin.uploaderTeam.stats.flagged')} value={String(d.stats.flagged)} tone="peach" />
        <MiniStat label={t('admin.uploaderTeam.stats.avgTime')} value={`${d.stats.today.avgTimeToUploadHours}h`} tone="sky" />
        <MiniStat label={t('admin.uploaderTeam.detail.pendingQueueLabel')} value={String(d.pendingQueueCount)} tone="lavender" />
        <MiniStat label={t('admin.uploaderTeam.perUploader.oldestHours')} value={`${d.stats.today.oldestPendingHours}h`} tone={d.stats.today.oldestPendingHours >= 24 ? 'rose' : 'mint'} />
      </div>

      {/* Hourly chart card */}
      <div className="rounded-xl bg-white border border-nav-card shadow-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-nav-navy flex items-center gap-1.5">
            <TrendingUp className="h-4 w-4 text-nav-tealDark" aria-hidden />
            {t('admin.uploaderTeam.stats.hourlyTitle')}
          </h4>
          <span className="text-[11px] text-nav-slate tabular-nums">{hourlyTotal}</span>
        </div>
        {hourlyTotal === 0 ? (
          <p className="text-xs italic text-nav-slate py-4 text-center">{t('admin.uploaderTeam.stats.hourlyEmpty')}</p>
        ) : (
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={hourly} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#6b7c8c' }} axisLine={false} tickLine={false} interval={3} />
                <ReTooltip
                  contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #eef1f3' }}
                  labelFormatter={(h) => `${h}:00`}
                />
                <Line type="monotone" dataKey="count" stroke="#228a8d" strokeWidth={2} dot={{ r: 2, fill: '#228a8d' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Comparison row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl bg-nav-sageTint border border-nav-sage p-4">
          <p className="text-[11px] uppercase tracking-wide text-nav-sageText opacity-80 flex items-center gap-1">
            <Clock className="h-3 w-3" aria-hidden />
            {t('admin.uploaderTeam.stats.avgTime')}
          </p>
          <p className="text-2xl font-semibold text-nav-sageText tabular-nums mt-1">{myAvg}h</p>
          <p className="text-[11px] text-nav-sageText opacity-80 mt-0.5">
            {t('admin.uploaderTeam.stats.teamAvg')}: <span className="tabular-nums">{teamAvg}h</span>
          </p>
          <p className="text-[11px] text-nav-sageText opacity-80 mt-0.5 italic">{vsTeam}</p>
        </div>

        <div className="rounded-xl bg-nav-amberTint border border-nav-amber/40 p-4">
          <p className="text-[11px] uppercase tracking-wide text-nav-amberText opacity-80 flex items-center gap-1">
            <Clock className="h-3 w-3" aria-hidden />
            {t('admin.uploaderTeam.stats.longestPending')}
          </p>
          <p className="text-2xl font-semibold text-nav-amberText tabular-nums mt-1">
            {d.longestPendingHours}h
          </p>
          <p className="text-[11px] text-nav-amberText opacity-80 mt-0.5">
            {t('admin.uploaderTeam.detail.pendingQueueLabel')}: <span className="tabular-nums">{d.pendingQueueCount}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone }: { label: string; value: string; tone: 'mint' | 'peach' | 'sky' | 'lavender' | 'rose' }): JSX.Element {
  const toneCls: Record<string, string> = {
    mint: 'bg-nav-sageTint border-nav-sage text-nav-sageText',
    peach: 'bg-nav-amberTint border-nav-amber/40 text-nav-amberText',
    sky: 'bg-nav-tealTint border-[#c9e2e2] text-nav-tealDark',
    lavender: 'bg-nav-tealTint border-[#c9e2e2] text-nav-tealDark',
    rose: 'bg-nav-redTint border-[#e3b5ae] text-nav-red',
  };
  return (
    <div className={cn('rounded-xl border p-3', toneCls[tone])}>
      <p className="text-[11px] uppercase tracking-wide opacity-80">{label}</p>
      <p className="text-xl font-semibold tabular-nums mt-1">{value}</p>
    </div>
  );
}

// =====================================================================
// Recent work panel — enriched cards
// =====================================================================

function formatTimeDelta(hours: number | null, dash: string): string {
  if (hours == null) return dash;
  if (hours < 1) {
    const mins = Math.max(0, Math.round(hours * 60));
    return `${mins}m`;
  }
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function RecentWorkPanel({
  d,
  t,
  onOpen,
}: {
  d: UploaderDetails;
  t: ReturnType<typeof useT>;
  onOpen: (id: string) => void;
}): JSX.Element {
  if (!d.recentWork || d.recentWork.length === 0) {
    return (
      <p className="text-sm text-nav-slate italic py-6 text-center">
        {t('admin.uploaderTeam.recentWork.empty')}
      </p>
    );
  }
  const dash = t('admin.uploaderTeam.recentWork.noDelta');
  return (
    <ul className="space-y-2.5">
      {d.recentWork.map((r) => (
        <li
          key={r.id}
          className="rounded-xl border border-nav-card bg-white shadow-sm p-3 hover:bg-nav-tealTint/40 cursor-pointer transition-colors"
          onClick={() => onOpen(r.id)}
        >
          <div className="flex items-start gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-nav-navy">{r.clientLastName || '—'}</span>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-nav-softer border border-nav-card text-nav-navy font-mono text-[10px]">
                  CIN ··· {r.clientCinLast6}
                </span>
                <StatusPill status={r.status} t={t} />
              </div>
              <p className="text-[11px] text-nav-slate mt-0.5">
                {new Date(r.processedAt).toLocaleString()}
                <span className="text-nav-faint"> · </span>
                {formatRelative(r.processedAt)}
                {r.agentName && (
                  <>
                    <span className="text-nav-faint"> · </span>
                    {t('admin.uploaderTeam.recentWork.col.agent')}: {r.agentName}
                  </>
                )}
              </p>

              {/* Time delta + FindHelp ID line */}
              <div className="flex items-center gap-2 flex-wrap mt-1.5">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-nav-tealTint border border-[#c9e2e2] text-nav-tealDark text-[11px]">
                  <Clock className="h-3 w-3" aria-hidden />
                  {t('admin.uploaderTeam.recentWork.col.timeDelta')}: <span className="tabular-nums font-medium">{formatTimeDelta(r.timeToProcessHours, dash)}</span>
                </span>
                {r.findhelpId && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-nav-amberTint border border-nav-amber/40 text-nav-amberText font-mono text-[10px]">
                    FindHelp #{r.findhelpId}
                  </span>
                )}
              </div>

              {/* Notes / errors */}
              {r.notes && (
                <p className="text-[11px] italic text-nav-amberText bg-nav-amberTint border border-nav-amber/40 rounded px-2 py-1 mt-1.5">
                  <span className="font-medium not-italic">{t('admin.uploaderTeam.recentWork.noteLabel')}:</span> {r.notes}
                </p>
              )}
              {r.flaggedError && (
                <p className="text-[11px] text-nav-red bg-nav-redTint border border-[#e3b5ae] rounded px-2 py-1 mt-1.5">
                  <span className="font-medium">{t('admin.uploaderTeam.recentWork.errorLabel')}:</span> {r.flaggedError}
                </p>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function StatusPill({ status, t }: { status: string; t: ReturnType<typeof useT> }): JSX.Element {
  if (status === 'uploaded') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-nav-sageTint text-nav-sageText border border-nav-sage">{t('admin.uploaderTeam.recentWork.statusUploaded')}</span>;
  }
  if (status === 'flagged') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-nav-redTint text-nav-red border border-[#e3b5ae]">{t('admin.uploaderTeam.recentWork.statusFlagged')}</span>;
  }
  return <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2]">{t('admin.uploaderTeam.recentWork.noMedicaidBadge')}</span>;
}

// =====================================================================
// Audit trail panel — humanized cards, day-grouped if >10
// =====================================================================

function relativeMinutes(t: ReturnType<typeof useT>, occurredAt: string): string {
  const diffMs = Date.now() - new Date(occurredAt).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return t('admin.uploaderTeam.audit.relativeNow');
  if (mins < 60) return t('admin.uploaderTeam.audit.relativeMin', { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('admin.uploaderTeam.audit.relativeHr', { n: hrs });
  const days = Math.floor(hrs / 24);
  return t('admin.uploaderTeam.audit.relativeDay', { n: days });
}

function dayBucketLabel(t: ReturnType<typeof useT>, iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (dayStart.getTime() === today.getTime()) return t('admin.uploaderTeam.audit.dayToday');
  if (dayStart.getTime() === yesterday.getTime()) return t('admin.uploaderTeam.audit.dayYesterday');
  return d.toLocaleDateString();
}

function AuditPanel({
  d,
  t,
  lang,
}: {
  d: UploaderDetails;
  t: ReturnType<typeof useT>;
  lang: 'en' | 'es';
}): JSX.Element {
  if (!d.audit || d.audit.length === 0) {
    return <p className="text-sm text-nav-slate italic py-6 text-center">{t('admin.uploaderTeam.audit.empty')}</p>;
  }

  const grouped = d.audit.length > 10;
  const buckets: Array<{ label: string; entries: typeof d.audit }> = [];
  if (grouped) {
    const map = new Map<string, typeof d.audit>();
    for (const a of d.audit) {
      const key = dayBucketLabel(t, a.occurredAt);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    for (const [label, entries] of map) buckets.push({ label, entries });
  } else {
    buckets.push({ label: '', entries: d.audit });
  }

  return (
    <div className="space-y-4">
      {buckets.map((b, idx) => (
        <div key={idx}>
          {b.label && (
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-[11px] uppercase tracking-wide font-medium text-nav-slate">{b.label}</h4>
              <span className="text-[10px] text-nav-faint tabular-nums">
                {t('admin.uploaderTeam.audit.entryCount', { n: b.entries.length })}
              </span>
            </div>
          )}
          <ul className="space-y-1.5">
            {b.entries.map((a) => {
              const cat = actionCategory(a.action);
              const tone = categoryTone(cat);
              const human = humanizeAction(a.action, lang);
              const entityHuman = humanizeEntity(a.entityType, lang);
              return (
                <li
                  key={a.id}
                  className="rounded-lg border border-nav-card bg-white shadow-sm px-3 py-2 hover:bg-nav-tealTint/40 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border whitespace-nowrap shrink-0', tone)}>
                      {human}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] text-nav-navy truncate">
                        {entityHuman || '—'}
                        {a.entityId && (
                          <span className="ml-1 inline-flex items-center px-1 py-0 rounded bg-nav-amberTint border border-nav-amber/40 text-nav-amberText font-mono text-[10px]">
                            #{a.entityId}
                          </span>
                        )}
                      </p>
                      <p className="text-[10px] text-nav-slate tabular-nums">
                        {relativeMinutes(t, a.occurredAt)}
                        <span className="text-nav-faint"> · </span>
                        {new Date(a.occurredAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
